import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getPrintifyHealth } from '@/lib/printify/health'
import {
  getPrintifyShops,
  getPrintifyProducts,
  pickProductThumbnail,
} from '@/lib/printify/products'
import ExternalProductCard from '@/components/integrations/ExternalProductCard'

/**
 * Browse Printify products for the active brand (worker proxies API; token server-side).
 */
export default function PrintifyProductsList() {
  const { brandId } = useParams<{ brandId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [selectedShopId, setSelectedShopId] = useState<string>('')

  const printifyBase = brandId ? `/brands/${brandId}/integrations/printify` : '/integrations/printify'
  const backHref = brandId ? `/brands/${brandId}/settings` : '/brands/picker'

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['printify', 'health', brandId || 'legacy'],
    queryFn: () => getPrintifyHealth(brandId),
    enabled: !!brandId,
  })

  const connected =
    !!brandId && (health?.accounts ?? []).some((a) => a.status === 'connected' && a.brandId === brandId)

  const { data: shopsData, isLoading: shopsLoading } = useQuery({
    queryKey: ['printify', 'shops', brandId],
    queryFn: () => getPrintifyShops(brandId!),
    enabled: !!brandId && connected,
  })

  const shops = useMemo(() => shopsData?.shops ?? [], [shopsData?.shops])
  const shopIdFromUrl = searchParams.get('shopId')

  useEffect(() => {
    if (!shops.length) return
    if (shopIdFromUrl && shops.some((s) => String(s.id) === shopIdFromUrl)) {
      setSelectedShopId(shopIdFromUrl)
      return
    }
    const first = String(shops[0].id)
    setSelectedShopId(first)
    if (!shopIdFromUrl) {
      setSearchParams({ shopId: first }, { replace: true })
    }
  }, [shops, shopIdFromUrl, setSearchParams])

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['printify', 'products', brandId, selectedShopId, page],
    queryFn: () => getPrintifyProducts(brandId!, selectedShopId, page, 25),
    enabled: !!brandId && connected && !!selectedShopId,
  })

  const products = useMemo(() => productsData?.products ?? [], [productsData?.products])
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return products
    return products.filter((p) => {
      const title = (p.title || '').toLowerCase()
      const id = String(p.id ?? '').toLowerCase()
      return title.includes(q) || id.includes(q)
    })
  }, [products, searchQuery])

  const syncShopToUrl = (shopId: string) => {
    setSearchParams(shopId ? { shopId } : {}, { replace: true })
  }

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10 p-4">
          <h1 className="text-xl font-bold text-gray-900">Printify products</h1>
        </header>
        <main className="p-4">
          <p className="text-sm text-gray-600 mb-3">Choose a brand to view Printify products.</p>
          <Link to="/brands/picker" className="text-blue-600 font-medium underline">
            Pick a brand
          </Link>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-start gap-2">
          <Link
            to={backHref}
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-50 text-xl"
            aria-label="Back"
          >
            ←
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Printify products
              {productsData?.total != null ? (
                <span className="text-lg font-normal text-gray-600"> ({productsData.total})</span>
              ) : null}
            </h1>
            <p className="text-xs text-gray-500 mt-1">Live data from your Printify account</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {healthLoading ? (
          <p className="text-sm text-gray-500">Checking connection…</p>
        ) : !connected ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-950">
            <p className="font-medium mb-2">Printify not connected</p>
            <p className="text-xs mb-3 opacity-90">Connect your Printify API token in Settings for this brand.</p>
            <Link
              to={`/brands/${brandId}/settings`}
              className="inline-block bg-amber-800 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 text-center"
            >
              Open Settings
            </Link>
          </div>
        ) : shopsLoading ? (
          <p className="text-sm text-gray-500">Loading shops…</p>
        ) : shops.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
            No Printify shops found for this account.
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="printify-shop" className="block text-sm font-medium text-gray-700 mb-1">
                Printify shop
              </label>
              <select
                id="printify-shop"
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base touch-target-min-44 bg-white"
                value={selectedShopId}
                onChange={(e) => {
                  const v = e.target.value
                  setSelectedShopId(v)
                  setPage(1)
                  syncShopToUrl(v)
                }}
              >
                {shops.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.title || `Shop ${s.id}`} ({s.id})
                  </option>
                ))}
              </select>
            </div>

            <input
              type="search"
              placeholder="Search products…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
              aria-label="Search products"
            />

            {productsLoading ? (
              <p className="text-sm text-gray-500">Loading products…</p>
            ) : (
              <>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <p className="text-gray-600">
                      {searchQuery.trim() ? 'No products match your search.' : 'No products in this shop.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredProducts.map((p) => (
                      <ExternalProductCard
                        key={String(p.id)}
                        to={`${printifyBase}/${selectedShopId}/${String(p.id)}`}
                        title={String(p.title || 'Untitled')}
                        idLabel="id"
                        idValue={String(p.id)}
                        statusLabel={
                          p.visible === false ? 'draft' : p.status ? String(p.status) : 'published'
                        }
                        statusKind={p.visible === false ? 'draft' : 'active'}
                        thumbnailUrl={pickProductThumbnail(p)}
                      />
                    ))}
                  </div>
                )}

                {!searchQuery.trim() && productsData && productsData.lastPage > 1 && (
                  <div className="flex justify-between items-center pt-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((x) => Math.max(1, x - 1))}
                      className="px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-40 touch-target-min-44"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {productsData.currentPage} of {productsData.lastPage}
                    </span>
                    <button
                      type="button"
                      disabled={page >= productsData.lastPage}
                      onClick={() => setPage((x) => x + 1)}
                      className="px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-40 touch-target-min-44"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  )
}
